import React, { useMemo } from 'react';

import { getIn, useFormikContext } from 'formik';

import { useManagementPackContext } from '../ManagementPackContext';

import { selectClassName, SELECT_BACKGROUND, SettingRow } from './FormField';

import { parseIsoDate } from 'Helpers/dateFormat';

import DatePickerField from 'CommonComponents/DatePickerField';
import MultiSelectField from 'CommonComponents/MultiSelectField';
import SelectField from 'CommonComponents/SelectField';
import ToggleInput from 'CommonComponents/ToggleInput';

const monthEnd = (period) => {
    const start = parseIsoDate(period);

    return start ? new Date(start.getFullYear(), start.getMonth() + 1, 0) : null;
};

/**
 * Cost centres grouped under their department, for the scope picker.
 */
const groupCostcentres = (costcentres) => {
    const groups = new Map();

    costcentres.forEach((costcentre) => {
        const key = costcentre.departmentId ?? 'none';

        if (!groups.has(key)) {
            groups.set(key, { label: costcentre.departmentName || $t('No department'), options: [] });
        }

        groups.get(key).options.push({ value: costcentre.value, label: costcentre.label });
    });

    return [...groups.values()];
};

const NoneAvailable = () => <div className="tw-h-9 tw-flex tw-items-center tw-justify-end tw-text-sm tw-text-charcoal-75">{$t('None available')}</div>;

const PackSettingsTab = () => {
    const { values, errors, setFieldValue } = useFormikContext();
    const { options, readOnly, movementBasisOptions, saveDraftSetting } = useManagementPackContext();

    const { settings, scope } = values;
    const periods = options.periods || [];
    const comparisonBudgets = options.comparisonBudgets || [];
    const costcentreGroups = useMemo(() => groupCostcentres(options.costcentres || []), [options.costcentres]);

    const period = periods.find((option) => option.value === settings.period);
    const minAsAt = parseIsoDate(settings.period);
    const maxAsAt = parseIsoDate(period?.defaultAsAt) || monthEnd(settings.period);

    const movementOptions = movementBasisOptions.map((option) => ({ value: option.id, label: option.label }));
    // Without a valid choice a draft uses the default option, if there is one;
    // an issued pack keeps the basis it was issued with, or none.
    const defaultMovementBasis = readOnly ? null : (movementBasisOptions.find((option) => option.isDefault)?.id ?? null);
    const movementValue = movementOptions.some((option) => option.value === settings.movement_basis_pack_id)
        ? settings.movement_basis_pack_id
        : defaultMovementBasis;

    const budgetUnitOptions = [
        { value: 'whole', label: $t('Whole numbers') },
        { value: 'thousands', label: $t('Thousands') },
        { value: 'millions', label: $t('Millions') },
    ];

    const error = (path) => getIn(errors, path);

    return (
        <div className="tw-flex tw-flex-col">
            <SettingRow
                label={$t('Reporting period')}
                help={$t('A calendar month from the project start; figures are captured when the pack is generated')}
                htmlFor="mp-period"
                error={error('settings.period')}
            >
                <SelectField
                    inputId="mp-period"
                    className={selectClassName}
                    backgroundColor={SELECT_BACKGROUND}
                    options={periods}
                    value={settings.period}
                    onChange={(option) => {
                        if (!option) {
                            return;
                        }
                        setFieldValue('settings.period', option.value);
                        setFieldValue('settings.as_at', option.defaultAsAt);
                    }}
                />
            </SettingRow>

            <SettingRow
                label={$t('As at date')}
                help={$t('Cut-off for actuals and overdue status; defaults to the last day of the period')}
                htmlFor="mp-as-at"
                error={error('settings.as_at')}
            >
                <DatePickerField
                    id="mp-as-at"
                    name="settings.as_at"
                    value={parseIsoDate(settings.as_at)}
                    min={minAsAt}
                    max={maxAsAt}
                    valueFormat="yyyy-MM-dd"
                    className="text-input !tw-h-9"
                    disabled={readOnly}
                    onChange={(name, value) => setFieldValue(name, value || null)}
                />
            </SettingRow>

            <SettingRow
                label={$t('Scope')}
                help={options.canUseOrgScope ? $t('The whole organisation, or selected cost centres') : $t('The cost centres the pack covers')}
                htmlFor={scope.org ? undefined : 'mp-costcentres'}
                error={error('scope.costcentreIds')}
                stacked
            >
                <div className="tw-flex tw-flex-col tw-gap-3">
                    {options.canUseOrgScope && (
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2" role="radiogroup" aria-label={$t('Scope')}>
                            <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50 tw-mb-0">
                                <input
                                    type="radio"
                                    name="mp-scope"
                                    checked={scope.org}
                                    onChange={() => setFieldValue('scope', { org: true, costcentreIds: [] })}
                                />
                                {$t('Organisation-wide')}
                            </label>
                            <label className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-font-semibold tw-text-charcoal-50 tw-mb-0">
                                <input
                                    type="radio"
                                    name="mp-scope"
                                    checked={!scope.org}
                                    onChange={() => setFieldValue('scope', { org: false, costcentreIds: scope.costcentreIds })}
                                />
                                {$t('Selected cost centres')}
                            </label>
                        </div>
                    )}
                    {!scope.org && (
                        <MultiSelectField
                            inputId="mp-costcentres"
                            className={selectClassName}
                            backgroundColor={SELECT_BACKGROUND}
                            options={costcentreGroups}
                            isGrouped
                            hideSelectedOptions={false}
                            clearable
                            value={scope.costcentreIds}
                            placeholder={$t('Select cost centres')}
                            onChange={(selected) =>
                                setFieldValue(
                                    'scope.costcentreIds',
                                    (selected || []).map((option) => option.value),
                                )
                            }
                        />
                    )}
                </div>
            </SettingRow>

            <SettingRow
                label={$t('Comparison budget')}
                help={$t('The locked budget version that variances are measured against')}
                htmlFor={comparisonBudgets.length ? 'mp-comparison-budget' : undefined}
            >
                {comparisonBudgets.length ? (
                    <SelectField
                        inputId="mp-comparison-budget"
                        className={selectClassName}
                        backgroundColor={SELECT_BACKGROUND}
                        options={comparisonBudgets}
                        value={settings.comparison_budget_id}
                        isDisabled={readOnly}
                        onChange={(option) => setFieldValue('settings.comparison_budget_id', option ? option.value : null)}
                    />
                ) : (
                    <NoneAvailable />
                )}
            </SettingRow>

            <SettingRow
                label={$t('Movement basis')}
                help={$t('The issued pack that every "since last report" figure compares to')}
                htmlFor={movementOptions.length ? 'mp-movement-basis' : undefined}
            >
                {movementOptions.length ? (
                    <SelectField
                        inputId="mp-movement-basis"
                        className={selectClassName}
                        backgroundColor={SELECT_BACKGROUND}
                        options={movementOptions}
                        value={movementValue}
                        isDisabled={readOnly}
                        onChange={(option) => setFieldValue('settings.movement_basis_pack_id', option ? option.value : null)}
                    />
                ) : (
                    <NoneAvailable />
                )}
            </SettingRow>

            <SettingRow label={$t('Confidentiality label')} htmlFor="mp-confidentiality" error={error('settings.confidentiality_label')}>
                <input
                    id="mp-confidentiality"
                    type="text"
                    className="text-input tw-border-0 tw-w-full"
                    maxLength={100}
                    value={settings.confidentiality_label}
                    disabled={readOnly}
                    onChange={(event) => setFieldValue('settings.confidentiality_label', event.target.value)}
                />
            </SettingRow>

            <SettingRow
                label={$t('Budget figures in')}
                help={$t('Budget, estimate, committed, actual, risk and contingency figures. Other figures always print in full.')}
                htmlFor="mp-budget-unit"
                error={error('settings.budget_unit')}
            >
                <SelectField
                    inputId="mp-budget-unit"
                    className={selectClassName}
                    backgroundColor={SELECT_BACKGROUND}
                    options={budgetUnitOptions}
                    value={settings.budget_unit || 'whole'}
                    isDisabled={readOnly}
                    onChange={(option) => setFieldValue('settings.budget_unit', option ? option.value : 'whole')}
                />
            </SettingRow>

            <SettingRow label={$t('Commentary box on every page')} help={$t("Same position on each page; carries forward last month's text")}>
                <div className="tw-flex tw-justify-end">
                    <ToggleInput
                        label={<span className="tw-sr-only">{$t('Commentary box on every page')}</span>}
                        noBottomSpacing
                        checked={!!settings.commentary_enabled}
                        disabled={readOnly}
                        onChange={() => setFieldValue('settings.commentary_enabled', !settings.commentary_enabled)}
                    />
                </div>
            </SettingRow>

            <SettingRow
                label={$t('Draft commentary from the snapshot')}
                help={
                    settings.commentary_enabled
                        ? $t("Writes a first draft of each page's commentary from its own figures, inside inbudget")
                        : $t('Turn on the commentary box to draft commentary')
                }
            >
                <div className="tw-flex tw-justify-end">
                    <ToggleInput
                        label={<span className="tw-sr-only">{$t('Draft commentary from the snapshot')}</span>}
                        noBottomSpacing
                        checked={!!settings.draft_commentary}
                        disabled={readOnly || !settings.commentary_enabled}
                        onChange={() => {
                            const next = !settings.draft_commentary;

                            setFieldValue('settings.draft_commentary', next);
                            saveDraftSetting(next);
                        }}
                    />
                </div>
            </SettingRow>
        </div>
    );
};

export default PackSettingsTab;
